import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ToggleWithLabel } from "./Checkbox";
import UsageBar from "./UsageBar";
import prettyBytes from "pretty-bytes";

// Charts
import { Line, Column } from "@ant-design/charts";
import type { LineConfig, ColumnConfig } from "@ant-design/charts";

import { useSetState } from "ahooks";
import { RootState } from "store";
import { arrowUp, arrowDown } from "ionicons/icons";
import { getDataCapDisplayValues, insertUrlParam } from "utils";
import { IonIcon, IonLoading } from "@ionic/react";
import { DataUsagePoint, useDataUsage } from "utils/hooks";
import { Link, useLocation } from "react-router-dom";
import Stat from "./Stat";
import {
  format,
  getDaysInMonth,
  isWithinInterval,
  sub,
  subWeeks,
} from "date-fns";
import { getPreviousDateByDay } from "utils/time";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
interface Props {
  id: string;
  type: "device" | "adapter" | "profile";
  chartType?: "line" | "bar";
}

interface Options {
  span: State["chartSpan"];
  today: Date;
  startDateDay: number;
}
export function getBillingPeriodPosition({
  span,
  today,
  startDateDay,
}: Options) {
  let position: number | undefined;
  const todaysDay = today.getDate();
  if (span === 30 || (span === 7 && todaysDay - startDateDay <= 7)) {
    // Create a new date object to avoid mutating the original
    const billingDate = new Date(today);

    // Validate startDateDay is a valid day of the month
    if (startDateDay >= 1 && startDateDay <= 31) {
      billingDate.setDate(startDateDay);
      // Check if the date is valid (e.g., Feb 30 would be invalid)
      if (
        billingDate.getDate() === startDateDay ||
        (startDateDay > 28 && billingDate.getMonth() !== today.getMonth())
      ) {
        return billingDate.getTime();
      }
    }
    return undefined;
  }
  return position;
}

interface State {
  chartSpan: 7 | 1 | 30;
  data: Array<{
    time: number;
    value: number;
    category: "Current usage" | "Previous usage";
    download: number;
    upload: number;
  }>;
  startFromBillingDate: boolean;
}

const UsageChart: React.FC<Props> = ({ id, type, chartType = "line" }) => {
  const { t } = useTranslation();
  const tabs = useMemo<
    {
      name: string;
      href: string;
      chartSpan: State["chartSpan"];
    }[]
  >(
    () => [
      {
        name: t("profileOverview:usageTabs.today"),
        href: "#today",
        chartSpan: 1,
      },
      {
        name: t("profileOverview:usageTabs.sevenDays"),
        href: "#7-days",
        chartSpan: 7,
      },
      {
        name: t("profileOverview:usageTabs.thirtyDays"),
        href: "#30-days",
        chartSpan: 30,
      },
    ],
    [t]
  );

  const [includePreviousPeriodStats, setIncludePreviousPeriodStats] =
    useState<boolean>(false);

  //TODO refactor, use native useState hook, get rid of useSetState
  const [state, setState] = useSetState<State>({
    chartSpan: 7,
    data: [],
    startFromBillingDate: true,
  });
  const today = useMemo(() => new Date(), []);
  const { dataCapOrigin } = useSelector((state: RootState) => ({
    dataCapOrigin: state.adapter.adapter!.settings.datacap,
  }));
  const search = useLocation().search;
  const query = useMemo(() => new URLSearchParams(search), [search]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dataCapDay = useMemo(() => dataCapOrigin.day, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dataCap = useMemo(() => dataCapOrigin.cap * 1000 * 1000, []);

  const { chartSpan } = state;
  useEffect(() => {
    if (query.has("span")) {
      const queryChartSpan = query.get("span");
      if (queryChartSpan) {
        const sp = +queryChartSpan;
        if (sp === 1 || sp === 7 || sp === 30) {
          setState({ chartSpan: sp });
        }
      }
    }
    if (query.has("show_previous")) {
      const showPrevious = query.get("show_previous");
      if (showPrevious && showPrevious === "true") {
        setIncludePreviousPeriodStats(true);
      }
    }
    if (query.has("start_from_billing")) {
      const startFromBillingDate = query.get("start_from_billing");
      if (startFromBillingDate) {
        if (startFromBillingDate === "true") {
          setState({ startFromBillingDate: true });
        }
      }
    }
  }, [query, chartSpan, setState]);
  const canStartFromBillingDate = useMemo(() => {
    return (
      type === "adapter" &&
      (chartSpan === 30 ||
        (chartSpan === 7 &&
          isWithinInterval(getPreviousDateByDay(dataCapDay, today), {
            start: subWeeks(today, 1),
            end: today,
          })))
    );
  }, [chartSpan, dataCapDay, today, type]);
  const { data, loading, error, stats, currentPeriodTotalUsage } = useDataUsage(
    {
      chartSpan,
      id,
      type,
      today,
      startFromBillingDate: state.startFromBillingDate,
      canStartFromBillingDate,
    }
  );

  const billingPeriodDate = useMemo<number | undefined>(
    () =>
      getBillingPeriodPosition({
        span: state.chartSpan,
        today: new Date(new Date(today).setHours(0)),
        startDateDay: dataCapDay,
      }),
    [dataCapDay, state.chartSpan, today]
  );
  const dataToDisplay = useMemo(() => {
    if (includePreviousPeriodStats) {
      return data;
    } else {
      return data.filter(({ category }) => category === "Current usage");
    }
  }, [includePreviousPeriodStats, data]);

  // Ensure chart data is valid to prevent encoding errors
  const validChartData = useMemo(() => {
    const fallbackData = [
      {
        time: Date.now(),
        value: 0,
        category: "Current usage" as const,
        download: 0,
        upload: 0,
        timestamp: Math.floor(Date.now() / 1000),
      },
    ];

    if (!dataToDisplay || dataToDisplay.length === 0) {
      return fallbackData;
    }

    // Validate data structure and filter out invalid entries
    const filteredData = dataToDisplay.filter(
      (item) =>
        item &&
        typeof item === "object" &&
        typeof item.time === "number" &&
        !isNaN(item.time) &&
        item.time > 0 && // Ensure positive timestamp
        typeof item.value === "number" &&
        !isNaN(item.value) &&
        item.value >= 0 && // Ensure non-negative value
        item.category &&
        typeof item.download === "number" &&
        !isNaN(item.download) &&
        item.download >= 0 &&
        typeof item.upload === "number" &&
        !isNaN(item.upload) &&
        item.upload >= 0
    );

    // Additional validation for 30-day + data cap scenario
    if (chartSpan === 30 && dataCap) {
      // Additional safety check: ensure no duplicate time values that could confuse the chart
      const uniqueTimeMap = new Map();
      const deduplicatedData = [];
      for (const item of filteredData) {
        if (!uniqueTimeMap.has(item.time)) {
          uniqueTimeMap.set(item.time, true);
          deduplicatedData.push(item);
        }
      }

      // Use deduplicated data for 30-day scenario
      if (deduplicatedData.length === 0) {
        return fallbackData;
      }

      return deduplicatedData.sort((a, b) => a.time - b.time); // Ensure chronological order
    }

    // If all data was filtered out, provide fallback
    if (filteredData.length === 0) {
      console.warn("All chart data was invalid, using fallback data");
      return fallbackData;
    }

    return filteredData.sort((a, b) => a.time - b.time); // Ensure chronological order
  }, [dataToDisplay, chartSpan, dataCap]);

  // Calculate noData early - we'll update this after safeDataForCharts is available
  const noData = useMemo(
    () => validChartData.every((item) => item.value === 0),
    [validChartData]
  );

  // @ts-ignore
  const annotations: LineConfig["annotations"] = useMemo(() => {
    // const cap = getDataCapDisplayValues(dataCapOrigin);
    const daysInMonth = getDaysInMonth(new Date());
    const dailyDataCap = dataCap ? dataCap / daysInMonth : 0;
    const spanForDataCap =
      chartSpan === 30 ? daysInMonth : chartSpan === 7 ? 9 : chartSpan;

    // Validate calculations to prevent NaN or invalid values
    const capValue =
      ((chartSpan === 30 ? daysInMonth : chartSpan) * dailyDataCap) /
      1000 /
      1000;

    // Only proceed with spanCap calculation if we have valid values
    let spanCap;
    try {
      if (isNaN(capValue) || capValue < 0) {
        spanCap = { cap: 0, unit: "GB", day: 1, capOriginal: 0 };
      } else {
        spanCap = getDataCapDisplayValues({
          cap: capValue,
          day: 1,
          since: 1,
        });
      }
    } catch (error) {
      console.warn("Error calculating spanCap:", error);
      spanCap = { cap: 0, unit: "GB", day: 1, capOriginal: 0 };
    }

    const todaysDay = today.getDate();

    // Validate billingPeriodDate and calculate positions safely
    const validBillingPeriodDate =
      billingPeriodDate && !isNaN(billingPeriodDate) ? billingPeriodDate : null;
    const validDailyDataCap =
      dailyDataCap && !isNaN(dailyDataCap) ? dailyDataCap : 0;
    const validSpanForDataCap =
      spanForDataCap && !isNaN(spanForDataCap) ? spanForDataCap : 0;

    // Calculate total data cap value early for use in annotations
    const totalDataCapValue =
      (chartSpan === 30 ? daysInMonth : chartSpan) * validDailyDataCap;

    let dataCapPositionStart = validBillingPeriodDate
      ? [validBillingPeriodDate, validDailyDataCap]
      : null;

    // Ensure dataCapPositionEnd doesn't contain NaN
    const endValue = validSpanForDataCap * validDailyDataCap;
    let dataCapPositionEnd = ["max", isNaN(endValue) ? 0 : endValue];
    let lastMonthBillingPeriodDate;

    if (
      validBillingPeriodDate &&
      todaysDay <= new Date(validBillingPeriodDate).getDate()
    ) {
      try {
        lastMonthBillingPeriodDate = sub(validBillingPeriodDate, {
          months: 1,
        }).getTime();
        if (!isNaN(lastMonthBillingPeriodDate)) {
          dataCapPositionStart = [
            lastMonthBillingPeriodDate,
            validDailyDataCap,
          ];
        }
      } catch (error) {
        console.warn("Error calculating lastMonthBillingPeriodDate:", error);
        lastMonthBillingPeriodDate = null;
      }
    }
    // Create billing date annotation - only if billing date is within current chart's time range
    let billingDateAnnotation = null;
    if (
      dataCap &&
      validBillingPeriodDate &&
      canStartFromBillingDate &&
      !noData
    ) {
      const billingTimestamp =
        lastMonthBillingPeriodDate || validBillingPeriodDate;

      // Check if billing date is within the current chart's time range
      const dataTimeRange =
        validChartData.length > 0
          ? {
              min: Math.min(...validChartData.map((d) => d.time)),
              max: Math.max(...validChartData.map((d) => d.time)),
            }
          : { min: Date.now() - 86400000, max: Date.now() };

      const isBillingDateInRange =
        billingTimestamp >= dataTimeRange.min &&
        billingTimestamp <= dataTimeRange.max;

      if (isBillingDateInRange) {
        // Get the value range for the vertical line
        const dataValueRange =
          validChartData.length > 0
            ? {
                min: Math.min(...validChartData.map((d) => d.value)),
                max: Math.max(...validChartData.map((d) => d.value)),
              }
            : { min: 0, max: totalDataCapValue || 1000000000 };

        // G2 v5 format: vertical line at specific timestamp
        billingDateAnnotation = [
          // Line annotation
          {
            type: "line",
            data: [
              { time: billingTimestamp, value: dataValueRange.min },
              { time: billingTimestamp, value: dataValueRange.max },
            ],
            encode: { x: "time", y: "value" },
            style: {
              stroke: "#666",
              strokeWidth: 1,
            },
          },
          // Text annotation
          {
            type: "text",
            data: [{ time: billingTimestamp, value: dataValueRange.max }],
            encode: { x: "time", y: "value" },
            style: {
              text: "Billing date",
              textAlign: "right",
              fontSize: 12,
              fill: "#666",
              dx: -5,
            },
          },
        ];
      }
    }

    // Create data cap annotation - only on 30-day view
    let dataCapAnnotation = null;
    if (
      dataCap &&
      type === "adapter" &&
      !noData &&
      chartSpan === 30 && // Only show on 30-day view
      spanCap &&
      typeof spanCap.cap === "number" &&
      !isNaN(spanCap.cap) &&
      spanCap.cap > 0
    ) {
      if (!isNaN(totalDataCapValue) && totalDataCapValue > 0) {
        // Get the time range from the chart data for the horizontal line
        const dataTimeRange =
          validChartData.length > 0
            ? {
                min: Math.min(...validChartData.map((d) => d.time)),
                max: Math.max(...validChartData.map((d) => d.time)),
              }
            : { min: Date.now() - 86400000, max: Date.now() };

        // G2 v5 format: horizontal line at data cap value
        dataCapAnnotation = [
          // Line annotation
          {
            type: "line",
            data: [
              { time: dataTimeRange.min, value: totalDataCapValue },
              { time: dataTimeRange.max, value: totalDataCapValue },
            ],
            encode: { x: "time", y: "value" },
            style: {
              stroke: "#f5222d",
              strokeWidth: 1,
              lineDash: [4, 4],
            },
          },
          // Text annotation
          {
            type: "text",
            data: [{ time: dataTimeRange.min, value: totalDataCapValue }],
            encode: { x: "time", y: "value" },
            style: {
              text: `Monthly Data Cap (${_.round(spanCap.cap, 2)} ${
                spanCap.unit
              })`,
              fontSize: 12,
              fill: "#f5222d",
              dy: -5,
            },
          },
        ];
      }
    }

    return [billingDateAnnotation, dataCapAnnotation].filter(Boolean).flat();
  }, [
    dataCap,
    chartSpan,
    today,
    billingPeriodDate,
    canStartFromBillingDate,
    noData,
    type,
    validChartData,
  ]);

  // No need for additional filtering since we handle it in annotation creation
  const filteredAnnotations = annotations;

  // TODO split line and bar chart into separate components
  const lineChartConfig = useMemo<LineConfig>(() => {
    const baseConfig: LineConfig = {
      data: validChartData,
      height: 400,
      xField: "time",
      yField: "value",
      smooth: true,
      animation: false,
      seriesField: "category",
      tooltip: {
        title: "time",
        items: [
          (datum: any, index?: number, data?: any, column?: any) => {
            if (!datum || typeof datum !== "object") {
              return {
                color: "#FDA23F",
                name: "Current usage",
                value: 0,
                data: {
                  category: "Current usage",
                  value: 0,
                  download: 0,
                  upload: 0,
                },
              };
            }
            return {
              color: datum.category === "Current usage" ? "#FDA23F" : "black",
              name: datum.category || "Current usage",
              value: datum.value || 0,
              data: datum, // Pass through the full datum for custom rendering
            };
          },
        ],
      },
      interaction: {
        tooltip: {
          render: (event: any, { items, title }: any) => {
            if (!items || items.length === 0) return null;

            // Format the title
            let formattedTitle = title;
            const titleAsNumber = Number(title);
            if (!isNaN(titleAsNumber)) {
              const date = new Date(titleAsNumber);
              if (chartSpan === 1) {
                formattedTitle = format(date, "ha 'on' MMM dd");
              } else if (chartSpan === 7) {
                formattedTitle = format(date, "EEE, MMM dd");
              } else {
                formattedTitle = format(date, "MMM dd, yyyy");
              }
            }

            return `<div class="p-2">
              <span class="text-sm">${formattedTitle}</span>
              <div class="mt-2">
                ${items
                  .map((item: any, index: number) => {
                    const totalValue = item.data?.value || 0;
                    const download = item.data?.download || 0;
                    const upload = item.data?.upload || 0;
                    const category = item.data?.category || "Current usage";

                    return `<div class="mb-1 flex flex-row justify-between" key="${category}-${index}">
                    <div class="flex flex-row items-center">
                      <div class="rounded-full w-2 h-2 mr-2" style="background-color: ${
                        item.color
                      }"></div>
                      <span class="text-xs">
                        ${category}<span class="${
                      category === "Current usage" ? "ml-2" : ""
                    }">:</span>
                        <span class="text-green-500 ml-2 mr-1">↓</span>${prettyBytes(
                          download
                        )}
                        <span class="text-purple-500 ml-2 mr-1">↑</span>${prettyBytes(
                          upload
                        )}
                      </span>
                    </div>
                  </div>`;
                  })
                  .join("")}
              </div>
            </div>`;
          },
        },
      },
      point: {
        size: 5,
        shape: "diamond",
      },
      color: ["#FDA23F", "black"],
      annotations: filteredAnnotations,
      axis: {
        x: {
          labelFormatter: (datum: any) => {
            // datum might be a timestamp in milliseconds
            if (datum == null) return "";

            const timestamp = typeof datum === "number" ? datum : Number(datum);
            if (isNaN(timestamp)) return "";

            const date = new Date(timestamp);
            if (isNaN(date.getTime())) return "";

            try {
              if (chartSpan === 1) {
                // 'ha' format for today - hour with AM/PM
                return format(date, "ha");
              } else if (chartSpan === 7) {
                // 'EEE' format for 7 days - abbreviated day names like "Mon", "Tue"
                return format(date, "EEE");
              } else {
                // 'dd MMM' format for 30 days - day and month
                return format(date, "dd MMM");
              }
            } catch (error) {
              return "";
            }
          },
        },
        y: {
          labelFormatter: (datum: any) => {
            if (datum == null) return "";
            const numValue = Number(datum);
            if (isNaN(numValue)) return "";
            return prettyBytes(numValue);
          },
        },
      },
    };

    return baseConfig;
  }, [validChartData, filteredAnnotations, chartSpan]);

  const dataToDisplayFormattedForBarChart = validChartData.reduce(
    (acc: any, cur: any) => {
      const currentDayTrafficStats = includePreviousPeriodStats
        ? [
            {
              type: cur.category,
              category: cur.category,
              value: cur.value,
              upload: cur.upload,
              download: cur.download,
              time: cur.time,
              timestamp: cur.timestamp,
            },
          ]
        : [
            {
              type: "Upload",
              category: cur.category,
              value: cur.upload,
              summedUpUploadAndDownload: cur.value,
              time: cur.time,
              timestamp: cur.timestamp,
            },
            {
              type: "Download",
              category: cur.category,
              value: cur.download,
              summedUpUploadAndDownload: cur.value,
              time: cur.time,
              timestamp: cur.timestamp,
            },
          ];
      return [...acc, ...currentDayTrafficStats];
    },
    []
  );

  const barChartBarsColors = includePreviousPeriodStats
    ? ["#FDA23F", "#b9a8d4"]
    : ["#a3d2cc", "#FDA23F"];

  // TODO split line and bar chart into separate components
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const barChartConfig = useMemo<ColumnConfig>(
    () => ({
      data: dataToDisplayFormattedForBarChart,
      isStack: true,
      height: 400,
      autoFit: true,
      animation: false,
      xField: "time",
      yField: "value",
      seriesField: "type",
      interactions: [
        {
          type: "active-region",
          enable: false,
        },
      ],
      connectedArea: {
        style: (oldStyle: any, element: any) => {
          return {
            fill: "rgba(0,0,0,0.25)",
            stroke: oldStyle.fill,
            lineWidth: 0.5,
          };
        },
      },
      smooth: true,
      tooltip: {
        title: "time",
        items: [
          (datum: any, index?: number, data?: any, column?: any) => {
            if (!datum || typeof datum !== "object") {
              return {
                color: "#FDA23F",
                name: "Download",
                value: 0,
                data: {
                  type: "Download",
                  value: 0,
                  category: "Current usage",
                },
              };
            }
            return {
              color: datum.type === "Upload" ? "#a3d2cc" : "#FDA23F",
              name: datum.type || "Download",
              value: datum.value || 0,
              data: datum, // Pass through the full datum for custom rendering
            };
          },
        ],
      },
      interaction: {
        tooltip: {
          render: (event: any, { items, title }: any) => {
            if (!items || items.length === 0) return null;

            // Format the title
            let formattedTitle = title;
            const titleAsNumber = Number(title);
            if (!isNaN(titleAsNumber)) {
              const date = new Date(titleAsNumber);
              if (chartSpan === 1) {
                formattedTitle = format(date, "ha 'on' MMM dd");
              } else if (chartSpan === 7) {
                formattedTitle = format(date, "EEE, MMM dd");
              } else {
                formattedTitle = format(date, "MMM dd, yyyy");
              }
            }

            return `<div class="p-2">
              <span class="text-sm">${formattedTitle}</span>
              <div class="mt-2">
                ${
                  includePreviousPeriodStats
                    ? items
                        .map((item: any, index: number) => {
                          const totalValue = item.data?.value || 0;
                          const download = item.data?.download || 0;
                          const upload = item.data?.upload || 0;
                          const category =
                            item.data?.category || "Current usage";

                          return `<div class="mb-1 flex flex-row justify-between" key="${category}-${index}">
                      <div class="flex flex-row items-center">
                        <div class="rounded-full w-2 h-2 mr-2" style="background-color: ${
                          item.color
                        }"></div>
                        <span class="text-xs">
                          ${category}<span class="${
                            category === "Current usage" ? "ml-2" : ""
                          }">:</span>
                          <span class="text-green-500 ml-2 mr-1">↓</span>${prettyBytes(
                            download
                          )}
                          <span class="text-purple-500 ml-2 mr-1">↑</span>${prettyBytes(
                            upload
                          )}
                        </span>
                      </div>
                    </div>`;
                        })
                        .join("")
                    : `<div class="mb-1 flex flex-row justify-between">
                    <div class="flex flex-row items-center">
                      <span class="text-xs">
                        Usage<span class="ml-2">:</span>
                        <span class="text-green-500 ml-2 mr-1">↓</span>${prettyBytes(
                          items.find((i: any) => i.name === "Download")
                            ?.value || 0
                        )}
                        <span class="text-purple-500 ml-2 mr-1">↑</span>${prettyBytes(
                          items.find((i: any) => i.name === "Upload")?.value ||
                            0
                        )}
                      </span>
                    </div>
                  </div>`
                }
              </div>
            </div>`;
          },
        },
      },
      point: {
        size: 5,
        shape: "diamond",
      },
      color: barChartBarsColors,
      options: {},
      annotations: filteredAnnotations,
      legend: {
        layout: "horizontal",
        position: "top",
      },
      axis: {
        x: {
          labelFormatter: (datum: any) => {
            // datum might be a timestamp in milliseconds
            if (datum == null) return "";

            const timestamp = typeof datum === "number" ? datum : Number(datum);
            if (isNaN(timestamp)) return "";

            const date = new Date(timestamp);
            if (isNaN(date.getTime())) return "";

            try {
              if (chartSpan === 1) {
                // 'ha' format for today - hour with AM/PM
                return format(date, "ha");
              } else if (chartSpan === 7) {
                // 'EEE' format for 7 days - abbreviated day names like "Mon", "Tue"
                return format(date, "EEE");
              } else {
                // 'dd MMM' format for 30 days - day and month
                return format(date, "dd MMM");
              }
            } catch (error) {
              return "";
            }
          },
        },
        y: {
          labelFormatter: (datum: any) => {
            if (datum == null) return "";
            const numValue = Number(datum);
            if (isNaN(numValue)) return "";
            return prettyBytes(numValue);
          },
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      validChartData,
      filteredAnnotations,
      chartSpan,
      includePreviousPeriodStats,
      barChartBarsColors,
    ]
  );

  if (error) {
    return (
      <span className="text-red-500">
        Error loading data. Please try again later or contact support
      </span>
    );
  }
  if (loading && data.length === 0) {
    return <IonLoading message="Loading data..." isOpen></IonLoading>;
  }

  return (
    <div className="chart-container">
      <div className="block">
        <nav className="flex space-x-4" aria-label="Tabs">
          {tabs.map((tab) => (
            <Link
              key={tab.name}
              to={`?tab=usage&span=${tab.chartSpan}`}
              className={classNames(
                tab.chartSpan === chartSpan
                  ? "bg-gray-100 text-gray-700"
                  : "text-gray-500 hover:text-gray-700",
                "no-underline px-3 py-2 font-medium text-sm rounded-md",
                "focus:ring focus:ring-orange"
              )}
              onClick={() => {
                setState({ chartSpan: tab.chartSpan });
              }}
              aria-current={tab.chartSpan === chartSpan ? "page" : undefined}
            >
              {tab.name}
            </Link>
          ))}
        </nav>
      </div>
      <dl className="mt-5 grid grid-cols-1 rounded-lg bg-white overflow-hidden shadow divide-y divide-gray-200 md:grid-cols-2 md:divide-y-0 md:divide-x">
        {stats.map((item, index) => (
          <Stat
            className={classNames({
              "border-solid border-b border-gray-200 md:border-b-0 md:border-r":
                index === 0,
            })}
            key={`${item.stat}-${item.name}`}
            item={item}
            chartSpan={chartSpan}
            startFromBillingDate={state.startFromBillingDate}
          />
        ))}
      </dl>
      {type !== "adapter" && (
        <div className="my-4">
          <UsageBar
            chartSpan={chartSpan}
            startFromBillingDate={state.startFromBillingDate}
            id={id}
            type={type}
            total={currentPeriodTotalUsage}
          />
        </div>
      )}
      <div className="module-large">
        {/* Additional render guard for 30-day + data cap scenario */}
        {chartSpan === 30 && dataCap && validChartData.length === 0 && (
          <div className="flex items-center justify-center h-96 text-gray-500">
            <div className="text-center">
              <div className="text-lg font-medium">Loading chart data...</div>
              <div className="text-sm mt-2">
                Please wait while we process your usage data.
              </div>
            </div>
          </div>
        )}
        {!(chartSpan === 30 && dataCap && validChartData.length === 0) && (
          <>
            {chartType === "bar" && <Column {...barChartConfig} />}
            {chartType === "line" && <Line {...lineChartConfig} />}
          </>
        )}
        <ToggleWithLabel
          id="previous"
          checked={includePreviousPeriodStats}
          onChange={(includePreviousPeriodStats) => {
            setIncludePreviousPeriodStats(includePreviousPeriodStats);
            if (includePreviousPeriodStats) {
              insertUrlParam("show_previous", "true");
            } else {
              insertUrlParam("show_previous", "false");
            }
          }}
          label={`${t("profileOverview:usage.showPrevious")} ${
            chartSpan === 1 ? "day" : chartSpan === 7 ? "week" : "month"
          }`}
          className="mt-4"
        />
        {canStartFromBillingDate ? (
          // eslint-disable-next-line react/jsx-no-undef
          <ToggleWithLabel
            id="start-from-billing-date"
            checked={state.startFromBillingDate}
            onChange={(startFromBillingDate) => {
              setState({ startFromBillingDate });
              if (startFromBillingDate) {
                insertUrlParam("start_from_billing", "true");
              } else {
                insertUrlParam("start_from_billing", "false");
              }
            }}
            label={`${t(
              "profileOverview:usage.startFromBillingDate"
            )} (${format(getPreviousDateByDay(dataCapDay, today), "d MMM")})`}
            className="mt-1"
          />
        ) : null}
      </div>
    </div>
  );
};

export default UsageChart;
